import DateNavigator, { DateNavigatorProps } from './DateNavigator';
import React, { useEffect, useState } from 'react';

import Button from './Button';
import DatePicker from 'react-date-picker';
import { DateTime } from 'luxon';
import { PeriodEnum } from '../../enum/PeriodEnum';
import classNames from 'classnames';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { useTranslation } from 'react-i18next';

export interface DateRangePickerNavigatorProps extends DateNavigatorProps {
  detailView?: 'month' | 'year' | 'decade' | 'century';
  isDisable?: boolean;
  currentDate?: string;
  beginDate?: boolean;
  maxDate?: string;
  minDate?: string;
  intervalUnit?: 'hours' | 'days' | 'weeks' | 'months' | 'years';
  startOfUnit?: 'day' | 'week' | 'month' | 'year';
  endOfUnit?: 'day' | 'week' | 'month' | 'year';
  periodGranularity?: PeriodEnum;
  prevInterval?: number;
  nextInterval?: number;
  hasManuallyChangeRange?: boolean;
  onChangeRange: (startDate: string, endDate: string) => void;
  onChange: (date: string) => void;
}
type ValuePiece = Date | null;

type Value = ValuePiece | [ValuePiece, ValuePiece];

export default function DateRangePickerNavigator({
  startDate,
  endDate,
  maxDate,
  minDate,
  currentDate,
  startOfUnit = 'month',
  endOfUnit = 'month',
  periodGranularity = PeriodEnum.MONTHLY,
  prevInterval = 6,
  nextInterval = 6,
  intervalUnit = 'days',
  isDisable = false,
  hasNextDisabled,
  hasPrevDisabled,
  detailView = 'month',
  hasManuallyChangeRange,
  onChangeRange,
  onChange,
}: DateRangePickerNavigatorProps) {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const [date, setDate] = useState<Value>(currentDate ? new Date(currentDate) : null);

  useEffect(() => {
    if (endDate && startDate) {
      const provisioningDate =
        (hasManuallyChangeRange ? null : currentDate) ??
        DateTime.fromISO(startDate!)
          .plus({
            [intervalUnit]: periodGranularity === PeriodEnum.QUARTERLY ? 0 : nextInterval,
          })
          .setZone('Europe/Brussels')
          .toJSDate()
          .toISOString();
      if (provisioningDate < startDate) {
        setDate(new Date(startDate));
      } else if (provisioningDate > endDate) {
        setDate(new Date(endDate));
      } else {
        setDate(new Date(provisioningDate));
      }
    }
  }, [
    currentDate,
    endDate,
    hasManuallyChangeRange,
    intervalUnit,
    minDate,
    nextInterval,
    periodGranularity,
    prevInterval,
    startDate,
  ]);

  const handleClickOutside = (event: any) => {
    if (
      typeof event.target?.parentElement?.className === 'string' &&
      (event.target?.parentElement?.className as string).match('react-calendar')
    ) {
      return;
    }
    setIsOpen(false);
  };
  const ref = useDetectClickOutside({
    onTriggered: handleClickOutside,
  });

  const handleClickNext = () => {
    const provisioningEndDate = DateTime.fromISO(endDate!)
      .setZone('Europe/Brussels')
      .plus({
        [intervalUnit]:
          periodGranularity === PeriodEnum.QUARTERLY ? nextInterval : prevInterval + nextInterval,
      })
      .toJSDate()
      .toISOString();
    onChangeRange(
      DateTime.fromISO(periodGranularity === PeriodEnum.QUARTERLY ? provisioningEndDate : endDate!)
        .setZone('Europe/Brussels')
        .startOf(startOfUnit)
        .toJSDate()
        .toISOString(),
      DateTime.fromISO(
        provisioningEndDate > (maxDate ?? new Date().toISOString())
          ? (maxDate ?? new Date().toISOString())
          : provisioningEndDate
      )
        .setZone('Europe/Brussels')
        .endOf(endOfUnit)
        .toJSDate()
        .toISOString()
    );
  };

  const handleClickPrev = () => {
    const provisioningStartDate = DateTime.fromISO(startDate!)
      .setZone('Europe/Brussels')
      .minus({
        [intervalUnit]:
          periodGranularity === PeriodEnum.QUARTERLY ? prevInterval : prevInterval + nextInterval,
      })
      .toJSDate()
      .toISOString();
    onChangeRange(
      DateTime.fromISO(
        provisioningStartDate < (minDate ?? new Date().toISOString())
          ? (minDate ?? new Date().toISOString())
          : provisioningStartDate
      )
        .setZone('Europe/Brussels')
        .startOf(startOfUnit)
        .toJSDate()
        .toISOString(),
      DateTime.fromISO(
        periodGranularity === PeriodEnum.QUARTERLY ? provisioningStartDate : startDate!
      )
        .setZone('Europe/Brussels')

        .endOf(endOfUnit)
        .toJSDate()
        .toISOString()
    );
  };

  const handleClickButtonRangePicker = (e: React.MouseEvent) => {
    if (isDisable === false) {
      setIsOpen(!isOpen);
    }
  };

  const handleClickApply = () => {
    if (date && (date as Date).toISOString() !== currentDate) {
      onChangeRange(
        DateTime.fromISO((date as any as Date).toISOString())
          .setZone('Europe/Brussels')
          .minus({
            [intervalUnit]: periodGranularity === PeriodEnum.QUARTERLY ? 0 : prevInterval,
          })
          .startOf(startOfUnit)
          .toJSDate()
          .toISOString(),

        DateTime.fromISO((date as any as Date).toISOString())
          .setZone('Europe/Brussels')
          .plus({
            [intervalUnit]: periodGranularity === PeriodEnum.QUARTERLY ? 0 : nextInterval,
          })
          .endOf(endOfUnit)
          .toJSDate()
          .toISOString()
      );
      onChange((date as any as Date).toISOString());
    }

    setIsOpen(false);
  };

  const onChangeDate = (value: Value) => {
    setDate(value);
  };

  return (
    <div className="date-range-picker-navigator">
      <div
        role="button"
        onClick={handleClickButtonRangePicker}
        className={classNames('date-range-picker-navigator__button', {
          disabled: isDisable,
        })}
        ref={ref}
      >
        <DateNavigator
          className="date-range-picker-navigator__date-navigator"
          onClickNext={handleClickNext}
          onClickPrev={handleClickPrev}
          hasNextDisabled={hasNextDisabled}
          hasPrevDisabled={hasPrevDisabled}
          startDate={
            startDate
              ? DateTime.fromISO(startDate).setZone('Europe/Brussels').toJSDate().toISOString()
              : null
          }
          endDate={
            endDate
              ? DateTime.fromISO(endDate).setZone('Europe/Brussels').toJSDate().toISOString()
              : null
          }
        />
      </div>

      {isOpen === true && (
        <div className="date-range-picker-navigator__calendar">
          <DatePicker
            isOpen
            calendarIcon={null}
            onChange={onChangeDate}
            maxDetail={detailView}
            value={date ? date : null}
            locale={i18n.language}
            format="dd/MM/yyyy"
            shouldCloseCalendar={() => false}
            maxDate={
              maxDate ? DateTime.fromISO(maxDate).setZone('Europe/Brussels').toJSDate() : undefined
            }
            minDate={
              minDate ? DateTime.fromISO(minDate).setZone('Europe/Brussels').toJSDate() : undefined
            }
          />
          <div className="date-range-picker-navigator__calendar__action">
            <Button className="btn btn--primary" onClick={handleClickApply}>
              {t('common.apply')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
